import { Injectable } from '@angular/core';
import { IStreetBuilding, StreetBuilding } from '../../models/street-building.model';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';
import { SpinnerService } from '../spinner/spinner.service';
import { ToastService } from '../toast/toast.service';
import { UtilService } from '../util/util.service';
import { environment } from '../../../../environments/environment';
import { firstValueFrom, map } from 'rxjs';
import { HouseFlat } from '../../models/house-flat.model';
import { IServerResponse, ServerResponse } from '../../models/server-response.model';

@Injectable({
  providedIn: 'root'
})
export class StreetBuildingService {

  public loading = false;
  public streetsBuildings: StreetBuilding[] = [];
  private pendingLoad?: Promise<StreetBuilding[]>;
  private loaded = false;
  private activeLoads = 0;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    public spinnerService: SpinnerService,
    private toastService: ToastService,
    private utilService: UtilService
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR ensureLoaded                             */
  /* -------------------------------------------------------------------------- */
  // region ensureLoaded
  // Con houseFlatId, si esa casa no está en el caché (alta o cambio posterior) se vuelve a cargar
  async ensureLoaded(
    neighborhoodId: string,
    houseFlatId?: string
  ): Promise<StreetBuilding[]> {

    if (this.loaded && !(houseFlatId && !this.findHouseFlat(houseFlatId))) {
      return this.streetsBuildings;
    }

    return this.pendingLoad ?? this.getAll(neighborhoodId);

  }

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR findHouseFlat                            */
  /* -------------------------------------------------------------------------- */
  // region findHouseFlat
  findHouseFlat(houseFlatId: string): HouseFlat | undefined {

    for (const streetBuilding of this.streetsBuildings) {
      const houseFlat = streetBuilding.housesFlats.find(
        (item) => item.houseFlatId === houseFlatId
      );
      if (houseFlat) return houseFlat;
    }

    return undefined;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR getAll                               */
  /* -------------------------------------------------------------------------- */
  // region getAll
  // Con doNotDisturb la lista es parcial (sin casas en "No molestar"): se devuelve
  // sin guardarla en el caché compartido que usan los guards y las notificaciones
  async getAll(
    neighborhoodId: string,
    doNotDisturb: boolean = false
  ): Promise<StreetBuilding[]> {

    if (doNotDisturb) return (await this.fetchAll(neighborhoodId, true)) ?? [];

    const load: Promise<StreetBuilding[]> = this.fetchAll(neighborhoodId, false).then(
      (streetsBuildings) => {
        // Si la petición falla (null) se conserva el caché anterior
        if (streetsBuildings) {
          this.streetsBuildings = streetsBuildings;
          this.loaded = true;
        }
        return this.streetsBuildings;
      }
    ).finally(
      () => {
        if (this.pendingLoad === load) this.pendingLoad = undefined;
      }
    );

    this.pendingLoad = load;

    return load;

  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR fetchAll                              */
  /* -------------------------------------------------------------------------- */
  // region fetchAll
  private async fetchAll(
    neighborhoodId: string,
    doNotDisturb: boolean
  ): Promise<StreetBuilding[] | null> {

    this.activeLoads++;
    this.loading = true;

    try {

      let urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/streets-buildings`;

      // Oculta las casas en "No molestar", igual que guard-app al crear una visita
      if (doNotDisturb) {
        urlApi += '?doNotDisturb=true';
      }

      const token = await this.authService.getToken();
      const headers = this.utilService.headers(token);

      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ streetsBuildings: IStreetBuilding[] }>>(
          urlApi,
          { headers }
        ).pipe(
          map(
            res => new ServerResponse<{ streetsBuildings: IStreetBuilding[] }>(
              res
            )
          )
        )
      );

      console.log('StreetBuildings:', response);

      if (response.statusCode === 200) {
        return (response.data?.streetsBuildings || []).map(
          e => new StreetBuilding(e)
        );
      }

    } catch (error) {
      console.error('Error fetching street buildings:', error);

      this.toastService.show({
        header: 'Error al cargar calles y casas',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });

    } finally {
      this.activeLoads--;
      this.loading = this.activeLoads > 0;
    }

    return null;

  }
}
